import { throwErrorMessage } from 'dashboard/store/utils/api';
import ConversationApi from '../../../../api/inbox/conversation';
import mutationTypes from '../../../mutation-types';
import { emitter } from 'shared/helpers/mitt';
import {
  isAceleresConversationStarted,
  isAceleresPersonalConversationForUser,
} from 'dashboard/helper/aceleresConversationState';

// ConversationCard (clique na lista) e MessagesView (montagem do chat) podem
// disparar markMessagesRead para a mesma conversa quase simultaneamente. Sem
// esse guard, os dois deltas otimistas de -1 se somam e o contador da caixa
// (ex.: Pessoais) zera antes do fetch real corrigir o valor. O mesmo guard é
// reaplicado a markMessagesUnread por simetria (menu de contexto/bulk action
// disparando duas vezes para a mesma conversa).
const pendingReadConversationIds = new Set();

const pendingUnreadConversationIds = new Set();

// Aceleres: em Novos, ABRIR a conversa nao conta como ler -- o nao-lido so sai
// quando o atendimento comeca.
//
// Novos e fila: a conversa esta ali justamente porque ninguem a assumiu, e o
// vendedor a espia para decidir. Zerar o nao-lido nessa espiada apaga o unico
// sinal de que ha mensagem esperando, e a conversa continua na caixa,
// indistinguivel das ja vistas.
//
// A regra e o ESTADO da conversa (nao iniciada), nao a aba aberta: a mesma
// conversa alcancada pela busca ou por link direto tem de responder igual.
// Pessoais fica de fora de proposito -- ali abrir marca lida desde 04/09/2026.
//
// Quem apaga o nao-lido e o inicio do atendimento: `ChatList#onAssignMine`
// chama markMessagesRead sem `auto`, e a partir dai a conversa esta iniciada e
// toda abertura volta a marcar. "Marcar como lida" do menu de contexto tambem
// continua passando, por ser decisao explicita.
const isAutoReadBlocked = ({
  auto,
  conversationId,
  conversation: payloadConversation,
  state,
  rootGetters,
}) => {
  if (!auto) return false;

  // A conversa vem no payload quando quem dispara ja a tem em maos (cartao da
  // lista, chat aberto). Sem isso, a abertura por link direto -- que monta o
  // chat antes de a lista existir -- nao acharia nada no store e a leitura
  // passaria.
  const conversation =
    payloadConversation ||
    (state?.allConversations || []).find(
      item => Number(item.id) === Number(conversationId)
    );
  if (!conversation) return false;
  if (isAceleresConversationStarted(conversation)) return false;

  const inboxId = Number(conversation.inbox_id ?? conversation.inboxId ?? 0);

  return !isAceleresPersonalConversationForUser({
    conversation,
    inbox: rootGetters['inboxes/getInbox'](inboxId),
    currentUserId: rootGetters.getCurrentUser?.id,
  });
};

export default {
  markMessagesRead: async ({ commit, state, rootGetters }, data) => {
    const conversationId = data?.id;
    if (
      isAutoReadBlocked({
        auto: data?.auto,
        conversationId,
        conversation: data?.conversation,
        state,
        rootGetters,
      })
    ) {
      return;
    }
    if (pendingReadConversationIds.has(conversationId)) return;
    pendingReadConversationIds.add(conversationId);

    try {
      // A caixa ativa viaja junto para que a resposta traga o unread_count
      // calculado pela MESMA regra da listagem que está na tela.
      const aceleresBox =
        data?.aceleresBox ?? state?.conversationFilters?.aceleresBox;
      const {
        data: {
          id,
          agent_last_seen_at: lastSeen,
          unread_count: unreadCount = 0,
        },
      } = await ConversationApi.markMessageRead({ ...data, aceleresBox });
      commit(mutationTypes.UPDATE_MESSAGE_UNREAD_COUNT, {
        id,
        lastSeen,
        unreadCount,
      });
      // O badge superior é derivado somente do snapshot autoritativo do
      // endpoint meta; não aplicar delta local evita que um evento antigo
      // ressuscite o valor anterior.
      emitter.emit('fetch_conversation_stats', {
        immediate: true,
        force: true,
      });
    } catch (error) {
      // Handle error
    } finally {
      pendingReadConversationIds.delete(conversationId);
    }
  },

  markMessagesUnread: async ({ commit }, { id }) => {
    if (pendingUnreadConversationIds.has(id)) return;
    pendingUnreadConversationIds.add(id);

    try {
      const {
        data: { agent_last_seen_at: lastSeen, unread_count: unreadCount },
      } = await ConversationApi.markMessagesUnread({ id });
      commit(mutationTypes.UPDATE_MESSAGE_UNREAD_COUNT, {
        id,
        lastSeen,
        unreadCount,
      });
      emitter.emit('fetch_conversation_stats', {
        immediate: true,
        force: true,
      });
    } catch (error) {
      throwErrorMessage(error);
    } finally {
      pendingUnreadConversationIds.delete(id);
    }
  },
};
